import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Helper: get auth token from Dynamic.xyz localStorage
function getAuthToken() {
  try {
    // Dynamic.xyz stores the JWT in localStorage
    const token = localStorage.getItem('dynamic_authentication_token')
      || localStorage.getItem('dynamic_token');
    return token;
  } catch {
    return null;
  }
}

// Helper: build headers with auth
function authHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// Payment form component
function PaymentForm({ amount, projectId, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Confirm payment with Stripe
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Record investment via backend
        const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/confirm`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
            projectId,
            amount
          })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          onSuccess(data);
        } else {
          setErrorMessage(
            data.error || 'Payment succeeded but recording failed. Please contact support.'
          );
        }
      }
    } catch (err) {
      setErrorMessage('Payment failed. Please try again.');
      console.error('Payment error:', err);
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
      <PaymentElement />

      {errorMessage && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '10px',
          color: '#fca5a5',
          fontFamily: "'Instrument Sans', system-ui, sans-serif",
          fontSize: '0.875rem'
        }}>
          {errorMessage}
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: '1rem',
        marginTop: '1.5rem'
      }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          style={{
            flex: 1,
            padding: '0.75rem',
            background: 'rgba(100, 116, 139, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '10px',
            color: '#CBD5E1',
            fontFamily: "'Instrument Sans', system-ui, sans-serif",
            fontWeight: '500',
            fontSize: '0.9375rem',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            opacity: isProcessing ? 0.5 : 1,
            transition: 'all 250ms ease'
          }}>
          Cancel
        </button>

        <button
          type="submit"
          disabled={!stripe || isProcessing}
          style={{
            flex: 1,
            padding: '0.75rem',
            background: isProcessing
              ? 'rgba(6, 182, 212, 0.4)'
              : '#06B6D4',
            border: 'none',
            borderRadius: '10px',
            color: '#0F172A',
            fontFamily: "'Instrument Sans', system-ui, sans-serif",
            fontWeight: '600',
            fontSize: '0.9375rem',
            cursor: isProcessing || !stripe ? 'not-allowed' : 'pointer',
            opacity: isProcessing || !stripe ? 0.5 : 1,
            transition: 'all 250ms ease'
          }}>
          {isProcessing ? 'Processing...' : `Invest $${amount}`}
        </button>
      </div>
    </form>
  );
}

// Main investment modal component
export default function InvestmentModal({ project, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [clientSecret, setClientSecret] = useState(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Use the projectId string field, not MongoDB _id
  const projectId = project.projectId || project._id || project.id;

  const handleAmountSubmit = async (e) => {
    e.preventDefault();

    const investmentAmount = parseFloat(amount);
    if (isNaN(investmentAmount) || investmentAmount < 10) {
      setError('Minimum investment is $10');
      return;
    }

    setIsCreatingIntent(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/create-intent`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          amount: investmentAmount,
          projectId
        })
      });

      const data = await response.json();

      if (data.success) {
        setClientSecret(data.clientSecret);
      } else {
        setError(data.error || 'Failed to create payment');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Create intent error:', err);
    }

    setIsCreatingIntent(false);
  };

  const handleInvestmentSuccess = (data) => {
    setSuccessData(data);
    if (onSuccess) {
      onSuccess(data);
    }
  };

  const stripeOptions = {
    clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#06B6D4',
        colorBackground: '#1E293B',
        colorText: '#F1F5F9',
        colorDanger: '#EF4444',
        fontFamily: "'Instrument Sans', system-ui, sans-serif",
        borderRadius: '10px'
      }
    }
  };

  // Funding progress info
  const fundingGoal = project.crowdfunding?.fundingGoal
    || project.budget?.fundingGoal
    || project.fundingGoal
    || 0;
  const currentAmount = project.crowdfunding?.currentAmount || 0;
  const progressPercent = fundingGoal > 0
    ? Math.min((currentAmount / fundingGoal) * 100, 100).toFixed(1)
    : 0;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(10, 10, 16, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={(e) => {
        // Close on backdrop click
        if (e.target === e.currentTarget && !successData) onClose();
      }}
    >
      <div style={{
        background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
        border: '1px solid rgba(6, 182, 212, 0.2)',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4), 0 0 30px rgba(6, 182, 212, 0.08)'
      }}>

        {/* ── Success State ─────────────────────────── */}
        {successData ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎬</div>
            <h2 style={{
              fontFamily: "'Outfit', system-ui, sans-serif",
              color: '#fff',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '0.5rem'
            }}>
              Investment Confirmed!
            </h2>
            <p style={{
              fontFamily: "'Instrument Sans', system-ui, sans-serif",
              color: '#94A3B8',
              fontSize: '0.9375rem',
              lineHeight: '1.55',
              marginBottom: '1.5rem'
            }}>
              You invested <strong style={{ color: '#06B6D4' }}>
                ${parseFloat(amount).toLocaleString()}
              </strong> in <strong style={{ color: '#F1F5F9' }}>
                {project.title}
              </strong>
            </p>

            {successData.projectProgress && (
              <div style={{
                padding: '1rem',
                background: 'rgba(6, 182, 212, 0.06)',
                border: '1px solid rgba(6, 182, 212, 0.15)',
                borderRadius: '10px',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontFamily: "'Instrument Sans', system-ui, sans-serif",
                  fontSize: '0.8125rem',
                  color: '#94A3B8',
                  marginBottom: '0.5rem'
                }}>
                  <span>{successData.projectProgress.progress} funded</span>
                  <span>
                    ${successData.projectProgress.currentAmount.toLocaleString()} / ${successData.projectProgress.fundingGoal.toLocaleString()}
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: successData.projectProgress.progress,
                    height: '100%',
                    background: 'linear-gradient(90deg, #06B6D4, #84CC16)',
                    borderRadius: '3px',
                    transition: 'width 600ms ease'
                  }} />
                </div>
                <div style={{
                  fontFamily: "'Instrument Sans', system-ui, sans-serif",
                  fontSize: '0.8125rem',
                  color: '#64748B',
                  marginTop: '0.5rem'
                }}>
                  {successData.projectProgress.investorCount} investor{successData.projectProgress.investorCount !== 1 ? 's' : ''}
                </div>
              </div>
            )}

            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#06B6D4',
                border: 'none',
                borderRadius: '10px',
                color: '#0F172A',
                fontFamily: "'Instrument Sans', system-ui, sans-serif",
                fontWeight: '600',
                fontSize: '0.9375rem',
                cursor: 'pointer',
                transition: 'all 250ms ease'
              }}>
              Done
            </button>
          </div>
        ) : (
          <>
            {/* ── Header ─────────────────────────────── */}
            <h2 style={{
              fontFamily: "'Outfit', system-ui, sans-serif",
              color: '#fff',
              marginBottom: '0.5rem',
              fontSize: '1.5rem',
              fontWeight: '700',
              letterSpacing: '-0.01em'
            }}>
              Invest in {project.title}
            </h2>
            <p style={{
              fontFamily: "'Instrument Sans', system-ui, sans-serif",
              color: '#94A3B8',
              marginBottom: '0.75rem',
              fontSize: '0.9375rem',
              lineHeight: '1.55'
            }}>
              Support this film and earn returns when it succeeds
            </p>

            {/* ── Funding Progress Bar ────────────────── */}
            {fundingGoal > 0 && (
              <div style={{
                padding: '0.75rem 1rem',
                background: 'rgba(6, 182, 212, 0.04)',
                border: '1px solid rgba(6, 182, 212, 0.1)',
                borderRadius: '10px',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontFamily: "'Instrument Sans', system-ui, sans-serif",
                  fontSize: '0.8125rem',
                  color: '#94A3B8',
                  marginBottom: '0.4rem'
                }}>
                  <span>{progressPercent}% funded</span>
                  <span>${currentAmount.toLocaleString()} / ${fundingGoal.toLocaleString()}</span>
                </div>
                <div style={{
                  width: '100%',
                  height: '4px',
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${progressPercent}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #06B6D4, #84CC16)',
                    borderRadius: '2px'
                  }} />
                </div>
              </div>
            )}

            {/* ── Amount Entry or Stripe Payment ──────── */}
            {!clientSecret ? (
              <form onSubmit={handleAmountSubmit}>
                <label style={{
                  display: 'block',
                  fontFamily: "'Instrument Sans', system-ui, sans-serif",
                  color: '#E2E8F0',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  fontSize: '0.9375rem'
                }}>
                  Investment Amount (USD)
                </label>

                {/* Quick amount buttons */}
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  marginBottom: '0.75rem'
                }}>
                  {[25, 50, 100, 250, 500].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setAmount(preset.toString())}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        background: parseFloat(amount) === preset
                          ? 'rgba(6, 182, 212, 0.15)'
                          : 'rgba(15, 23, 42, 0.6)',
                        border: parseFloat(amount) === preset
                          ? '1px solid rgba(6, 182, 212, 0.4)'
                          : '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '8px',
                        color: parseFloat(amount) === preset ? '#06B6D4' : '#94A3B8',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 200ms ease'
                      }}
                    >
                      ${preset}
                    </button>
                  ))}
                </div>

                <input
                  type="number"
                  min="10"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount (min $10)"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '10px',
                    color: '#F1F5F9',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '1rem',
                    marginBottom: '1rem',
                    transition: 'border-color 250ms ease, box-shadow 250ms ease',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(6, 182, 212, 0.4)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(6, 182, 212, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    e.target.style.boxShadow = 'none';
                  }}
                />

                {error && (
                  <div style={{
                    padding: '1rem',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '10px',
                    color: '#fca5a5',
                    fontFamily: "'Instrument Sans', system-ui, sans-serif",
                    fontSize: '0.875rem',
                    marginBottom: '1rem'
                  }}>
                    {error}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isCreatingIntent}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: 'rgba(100, 116, 139, 0.15)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '10px',
                      color: '#CBD5E1',
                      fontFamily: "'Instrument Sans', system-ui, sans-serif",
                      fontWeight: '500',
                      fontSize: '0.9375rem',
                      cursor: isCreatingIntent ? 'not-allowed' : 'pointer',
                      opacity: isCreatingIntent ? 0.5 : 1,
                      transition: 'all 250ms ease'
                    }}>
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isCreatingIntent || !amount}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: isCreatingIntent
                        ? 'rgba(6, 182, 212, 0.4)'
                        : '#06B6D4',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#0F172A',
                      fontFamily: "'Instrument Sans', system-ui, sans-serif",
                      fontWeight: '600',
                      fontSize: '0.9375rem',
                      cursor: isCreatingIntent || !amount ? 'not-allowed' : 'pointer',
                      opacity: isCreatingIntent || !amount ? 0.5 : 1,
                      transition: 'all 250ms ease'
                    }}>
                    {isCreatingIntent ? 'Processing...' : 'Continue to Payment'}
                  </button>
                </div>
              </form>
            ) : (
              <Elements stripe={stripePromise} options={stripeOptions}>
                <PaymentForm
                  amount={parseFloat(amount)}
                  projectId={projectId}
                  onSuccess={handleInvestmentSuccess}
                  onCancel={() => {
                    setClientSecret(null);
                  }}
                />
              </Elements>
            )}
          </>
        )}
      </div>
    </div>
  );
}
